import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TspApiService from "../../api/tspApiService";
import useTspApiResource from "../../hooks/useTspApiResource";
import { LIST_STATUS_FILTERS } from "../../constants/tspJourneyStatus";

export const PAGE_SIZE = 10;
// "All Journeys" is the default status (Journey Management and Journey List):
// every journey shows until a status is chosen.
export const DEFAULT_JOURNEY_FILTERS = Object.freeze({ q: "", temple: "", status: "all", stateId: "", teamId: "", year: "", from: "", to: "" });

// The applied filters + page as the API's query string (empty values omitted).
export function buildJourneyQuery(filters, page) {
  const query = { page, limit: PAGE_SIZE, sort: "start_date", order: "desc" };
  if (filters.q?.trim()) query.q = filters.q.trim();
  if (filters.temple?.trim()) query.temple = filters.temple.trim();
  const status = LIST_STATUS_FILTERS[filters.status]?.query;
  if (status) query.status = status;
  if (filters.stateId) query.state_id = filters.stateId;
  if (filters.teamId) query.team_id = filters.teamId;
  if (filters.year) query.year = filters.year;
  if (filters.from) query.from = filters.from;
  if (filters.to) query.to = filters.to;
  return query;
}

// The empty-list message for the applied filters (the list holds journey places).
// A year with no place of a journey in the chosen status says so, and points at
// the status filter (when one is chosen).
export function emptyJourneysMessage(filters) {
  if (!filters.year) return "No journey place matches these filters. Change them, or create a new journey.";
  const narrowed = filters.status && filters.status !== "all";
  const what = narrowed ? `places of ${LIST_STATUS_FILTERS[filters.status]?.label.toLowerCase() || "journeys"}` : "journey places";
  const hint = narrowed ? " Choose “All Journeys” to include every status." : "";
  return `No ${what} in ${filters.year} match these filters.${hint}`;
}

// Data for the Journeys screens: the filtered page of JOURNEY PLACES (one row per
// place of every journey — GET /tsp/journeys/places), and the headline summary
// (which also lists the teams/states/temples/years the filters can offer).
// The year is filtered by the server (?year=), never in the browser. The two
// load independently, so a summary failure never hides the journeys.
export default function useTspJourneysListData() {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialFilters = () => {
    const rawStatus = searchParams.get("status");
    let status = DEFAULT_JOURNEY_FILTERS.status;
    if (rawStatus === "past" || rawStatus === "completed") status = "completed";
    else if (rawStatus === "upcoming" || rawStatus === "planned") status = "planned";
    else if (rawStatus && LIST_STATUS_FILTERS[rawStatus]) status = rawStatus;

    return {
      q: searchParams.get("q") || "",
      temple: searchParams.get("temple") || "",
      status,
      stateId: searchParams.get("state_id") || "",
      teamId: searchParams.get("team_id") || "",
      year: /^\d{4}$/.test(searchParams.get("year") || "") ? searchParams.get("year") : "",
      from: searchParams.get("from") || "",
      to: searchParams.get("to") || "",
    };
  };

  const [filters, setFilters] = useState(getInitialFilters);
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const query = buildJourneyQuery(filters, page);

  const places = useTspApiResource(() => TspApiService.listJourneyPlaces(query), [JSON.stringify(query)]);
  const summary = useTspApiResource(() => TspApiService.getJourneySummary(), []);

  const applyFilters = (next) => {
    setFilters(next);
    setPage(1);
    const params = {};
    if (next.q) params.q = next.q;
    if (next.temple) params.temple = next.temple;
    if (next.status && next.status !== DEFAULT_JOURNEY_FILTERS.status) params.status = next.status;
    if (next.stateId) params.state_id = next.stateId;
    if (next.teamId) params.team_id = next.teamId;
    if (next.year) params.year = next.year;
    if (next.from) params.from = next.from;
    if (next.to) params.to = next.to;
    setSearchParams(params, { replace: true });
  };

  return { places, summary, filters, applyFilters, page, setPage };
}
